import { supabase } from '@/lib/supabase/client'
import type { Database, Json } from '@/types/database'

/**
 * Hand an app-defined object or array to a `jsonb` column.
 *
 * Our row shapes (`WorksheetScope[]`, `UploadedMeta[]`, `Record<string, unknown>`,
 * …) are structurally valid JSON, but TypeScript can't prove an interface
 * satisfies `Json`'s index signature, so a direct assignment fails. Use this at
 * the write boundary instead of scattering `as unknown as Json` casts.
 */
export function toJson<T>(value: T): Json {
  return value as unknown as Json
}

/**
 * Escape hatch for a PostgREST select whose relation nesting exceeds
 * TypeScript's instantiation budget (TS2589 — "type instantiation is
 * excessively deep").
 *
 * The table name is still checked; only the select string stops being parsed
 * into a row type, so assert the result (`as unknown as Row[]`) at the call
 * site as these queries already do. Prefer a SECURITY DEFINER RPC for anything
 * on a hot path — see the architecture rules on nested selects.
 */
export function deepSelect<T extends keyof Database['public']['Tables']>(table: T) {
  return supabase.from(table) as never as { select: (query: string) => any }
}
